import { useState } from 'react'
import { Box, Button, Container, Paper, TextField, Typography, Alert } from '@mui/material'
import PublicHeader from '../components/public/PublicHeader'
import { useLanguage } from '../contexts/LanguageContext'
import apiClient from '../api/client'

const Register = () => {
  const { t } = useLanguage()
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    passwordConfirm: '',
    company: '',
    message: ''
  })
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value })
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!formData.username || !formData.email || !formData.password) {
      setError(t('register.validation.required', 'Username, email, and password are required'))
      return
    }

    if (formData.password !== formData.passwordConfirm) {
      setError(t('register.validation.passwordMismatch', 'Passwords do not match'))
      return
    }

    if (formData.password.length < 6) {
      setError(t('register.validation.passwordLength', 'Password must be at least 6 characters'))
      return
    }

    try {
      setLoading(true)
      setError(null)

      await apiClient.post('/auth/registration-request', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        company: formData.company,
        message: formData.message
      })

      setSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Registration request failed')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
        <PublicHeader />
        <Container maxWidth="sm" sx={{ mt: 6 }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'success.main' }}>
              {t('register.success.title', 'Request Submitted!')}
            </Typography>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('register.success.message',
                'Thank you for your registration request! We will review your application and contact you within 24-48 hours.')}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('register.success.info', 'You will receive an email once your account is approved.')}
            </Typography>
          </Paper>
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      <PublicHeader />

      <Container maxWidth="sm" sx={{ mt: 6, mb: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            {t('nav.register', 'Request Access')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('register.info', 'Fill out the form below to request access to YourModel.eu. We will review your application and contact you soon.')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label={t('register.username', 'Username')}
              value={formData.username}
              onChange={handleChange('username')}
              required
              fullWidth
              autoComplete="username"
            />

            <TextField
              label={t('register.email', 'Email')}
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              required
              fullWidth
              autoComplete="email"
            />

            <TextField
              label={t('register.company', 'Company Name')}
              value={formData.company}
              onChange={handleChange('company')}
              fullWidth
              placeholder={t('register.companyPlaceholder', 'Your fashion brand or company')}
            />

            <TextField
              label={t('login.password', 'Password')}
              type="password"
              value={formData.password}
              onChange={handleChange('password')}
              required
              fullWidth
              autoComplete="new-password"
              helperText={t('register.passwordHint', 'At least 6 characters')}
            />

            <TextField
              label={t('register.repeatPassword', 'Confirm Password')}
              type="password"
              value={formData.passwordConfirm}
              onChange={handleChange('passwordConfirm')}
              required
              fullWidth
              autoComplete="new-password"
            />

            <TextField
              label={t('register.message', 'Why do you want access?')}
              multiline
              rows={3}
              value={formData.message}
              onChange={handleChange('message')}
              fullWidth
              placeholder={t('register.messagePlaceholder', 'Tell us about your use case...')}
            />

            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{ borderRadius: 2, mt: 1 }}
              fullWidth
            >
              {loading
                ? t('register.submitting', 'Submitting...')
                : t('register.submit', 'Submit Request')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default Register
